import React from 'react';

// Single source of truth for the CTVBuyer Advertiser Terms copy. Both the
// standalone /advertiser-terms page and the step-1 overlay (AdvertiserTermsModal)
// render this component, so the wording can never drift between the two.
//
// The document's formal title is "CTVBuyer Streamline Program Terms"; the
// surrounding UI (checkbox, page, modal header) uses the friendlier
// "CTVBuyer Advertiser Terms" label by product decision (CTV-639). The text
// below is transcribed verbatim from the legal source (CTVBuyer Advertiser
// Terms - 7.2.26.docx, last updated July 15, 2026) — do not reword it without
// a new approved revision, and bump ADVERTISER_TERMS_VERSION when it changes.

// Bump when the terms text changes. Surfaced in the footer so a reader (and,
// later, any recorded acceptance) can tell which revision they saw.
export const ADVERTISER_TERMS_VERSION = '2026-07-15';

// Human-facing effective date, matching the "Last updated" line in the source.
export const ADVERTISER_TERMS_EFFECTIVE_DATE = 'Last updated July 15, 2026';

const Section: React.FC<{ heading: string; children: React.ReactNode }> = ({ heading, children }) => (
  <section className="space-y-2">
    <h2 className="text-[15px] font-semibold text-slate-900 tracking-tight">{heading}</h2>
    <div className="text-[13px] leading-relaxed text-slate-600 space-y-2">{children}</div>
  </section>
);

// Bulleted sub-list used by the multi-part sections (Ads, Payment, etc.).
const Bullets: React.FC<{ items: React.ReactNode[] }> = ({ items }) => (
  <ul className="list-disc pl-5 space-y-2">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const AdvertiserTermsContent: React.FC = () => {
  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-[17px] font-semibold text-slate-900 tracking-tight">
          CTVBuyer Streamline Program Terms
        </h1>
        <p className="text-[11px] text-slate-400">Last updated: July 15, 2026</p>
      </div>

      <div className="text-[13px] leading-relaxed text-slate-600 space-y-2">
        <p>
          These Streamline Program Terms (&ldquo;Agreement&rdquo;) are a legally binding contract
          between CTVBuyer, LLC and Advertiser. &ldquo;Advertiser&rdquo; means either the individual
          who is accepting this Agreement on his or her own behalf, or the company or other legal
          entity on whose behalf this Agreement is being accepted.
        </p>
        <p>
          CTVBuyer may modify this Agreement by posting the revised version on this page or another
          identified page. If Advertiser does not accept the changes, Advertiser must stop using the
          Services. Advertiser&rsquo;s continued use of the Services after CTVBuyer publishes changes
          means that Advertiser agrees to the updates.
        </p>
      </div>

      <Section heading="Accounts">
        <p>
          Advertiser will keep its account information accurate and up to date. Advertiser is solely
          responsible for all users it permits to access its account and for all activity and amounts
          owed under its account. Advertiser will ensure that all users use its account solely on
          Advertiser&rsquo;s behalf and that they comply with this Agreement. Advertiser is liable for
          any breach of this Agreement by any user. Advertiser will promptly notify CTVBuyer of any
          accidental disclosure or unauthorized use of access credentials.
        </p>
      </Section>

      <Section heading="License">
        <p>
          Subject to compliance with the terms of this Agreement and payments of any amounts committed
          or owed, CTVBuyer grants Advertiser a limited, non-exclusive, non-transferable, and
          non-sublicensable license to (a) access and use the Services in the United States, on a
          self-serve basis, to manage ad campaigns via the CTVBuyer platform (&ldquo;Platform&rdquo;),
          solely for its own benefit and internal use, and (b) to use any information made available by
          the Services solely for the purpose of using the Services. CTVBuyer may offer additional
          features or services that require additional fees and/or terms, which will be set forth in the
          Platform or agreed separately by the parties.
        </p>
      </Section>

      <Section heading="Inventory and Other Third Party Services">
        <p>
          The Services enable advertisers to purchase ad placements on third party publisher digital
          media (&ldquo;Inventory&rdquo;) and may include other third party services (such publishers
          and other third parties, &ldquo;Providers&rdquo;). For clarity, CTVBuyer&rsquo;s
          &ldquo;Services&rdquo; do not include the Inventory or other third party services. If a
          Provider has additional requirements that Advertiser must follow, CTVBuyer will provide those
          terms to Advertiser in writing (e.g., via the Platform or email, etc.). This Agreement
          incorporates all such requirements.
        </p>
      </Section>

      <Section heading="Ads">
        <Bullets
          items={[
            <>
              Advertiser will ensure there is at least one approved Ad available for each campaign, at
              all times. Advertiser will continue to accrue charges regardless of the number of Ads
              available for a campaign. CTVBuyer may immediately pause or remove any Ad at any time in
              CTVBuyer&rsquo;s sole discretion.
            </>,
            <>
              If CTVBuyer makes advertising standards available to Advertiser in writing, those
              standards are incorporated into this Agreement and Advertiser will comply with those
              standards.
            </>,
            <>
              Advertiser will obtain all rights necessary to store and serve the advertisements
              associated with Advertiser&rsquo;s ad campaigns (&ldquo;Ads&rdquo;). Advertiser will not
              use the Services with Ads or other content, products or services that violate any
              applicable laws, regulations, third-party rights, or this Agreement. Advertiser will
              inform CTVBuyer within one business day of becoming aware of any lawsuit, threatened
              lawsuit, governmental investigation or other governmental action related to Advertiser or
              the Ads.
            </>,
            <>
              Advertiser represents and warrants that it owns or otherwise has all rights needed to
              create and use Ads. Advertiser represents and warrants that the Ads: (a) comply with all
              laws and regulations, (b) do not breach any third party rights, including intellectual
              property and privacy rights, and (c) do not include unlawful content or speech or
              malicious code (e.g., software viruses), any content encouraging, or assisting criminal
              offenses, or any content that could be considered threatening, abusive, harassing,
              defamatory, libelous, vulgar, violent, obscene, pornographic, drug related or racially,
              ethnically or otherwise objectionable.
            </>,
            <>
              CTVBuyer may assist Advertiser with generating Ads. Advertiser is solely responsible for
              all Ads (including all content and materials used for such Ads), including (a) their
              legality, accuracy, and appropriateness, and (b) ensuring Ads include any legally required
              disclosures or other elements. Any use of an Ad (including generating, saving, modifying
              and running campaigns) constitutes Advertiser&rsquo;s express approval of such Ad. As to
              any Ads that CTVBuyer creates or helps to create, Advertiser will use such Ads solely for
              campaigns with CTVBuyer and will not copy or attempt to export such Ads. Advertiser
              acknowledges that because of the nature of these Services, Ads created or assisted by
              CTVBuyer may not be unique and that the Services may generate the same or similar output
              for other customers. For the avoidance of doubt, Advertiser shall retain all rights in its
              original content and materials.
            </>,
          ]}
        />
      </Section>

      <Section heading="Reporting, Payment and Related Issues">
        <Bullets
          items={[
            <>
              CTVBuyer will provide Advertiser with campaign reporting via the Platform. Until CTVBuyer
              has closed out a given month, reporting information is preliminary and may not reflect
              final amounts. The number of impressions and amounts owed as tracked by CTVBuyer and its
              Providers shall be decisive.
            </>,
            <>
              Advertiser authorizes CTVBuyer to charge prepaid fees and any other amounts owed under this
              Agreement to Advertiser&rsquo;s credit card, via automatic ACH, or such other similar
              payment method, at CTVBuyer&rsquo;s option. Advertiser will provide its most current credit
              card and/or other information required to complete payment, and keep such information
              updated if it changes. Advertiser represents that it has authority to authorize CTVBuyer to
              charge the payment card/account for amounts owed to CTVBuyer by Advertiser. CTVBuyer may
              revise Advertiser&rsquo;s credit limit, require further prepayment, or adjust permitted
              payment methods at any time in its sole discretion. CTVBuyer may charge interest at a rate
              of 2% per month (or the legal maximum, whichever is lower) for late payments (including
              delays caused by inaccurate payment information or disputes). Advertiser is responsible for
              reasonable out-of-pocket costs that CTVBuyer incurs to collect unpaid amounts owed. All
              payment obligations are non-cancelable and all amounts paid for Services provided are
              non-refundable. If Advertiser requires a purchase order to make payments, Advertiser will
              include the corresponding purchase order number in the Platform. All amounts due must be
              paid without setoff or deduction.
            </>,
            <>
              Advertiser will not pays a Provider directly for Inventory or other services available via
              the CTVBuyer Services, including reach extension.
            </>,
            <>
              CTVBuyer uses Stripe (a third party service) to execute online payment transactions (e.g.,
              credit card, wire transfer, automatic ACH, etc.). Advertiser agrees to and will comply with
              all applicable Stripe terms, including the Stripe Connected Account Agreement
              (https://stripe.com/connect/account-terms), the Stripe Payment Method Terms
              (https://stripe.com/legal/payment-terms) and the Prohibited and Restricted Businesses
              (https://stripe.com/legal/restricted-businesses), as may be updated from time to time. For
              avoidance of doubt, if any payment Advertiser initiates using Stripe does not successfully
              complete, Advertiser is obligated to immediately pay CTVBuyer by another means acceptable to
              CTVBuyer and Advertiser is responsible for any costs, penalties or charges imposed by
              Stripe. Advertiser authorizes CTVBuyer to make repeating automatic payments using the credit
              card or other payment means provided to Stripe.
            </>,
            <>
              CTVBuyer&rsquo;s fees do not include any present or future local, state, federal or foreign
              taxes, levies, fees or duties of any nature (&ldquo;Taxes&rdquo;). CTVBuyer may charge
              applicable Taxes in addition to the fees due under this Agreement. Except for any Tax that
              CTVBuyer has charged to Advertiser (and that Advertiser has actually paid to CTVBuyer),
              Advertiser is responsible for remitting to the appropriate authority all Taxes based on or
              arising from this Agreement other than Taxes based on CTVBuyer&rsquo;s net income. All
              payments due under this Agreement shall be made without setoff or deduction on account of
              any Taxes unless Advertiser is required by law to withhold Taxes from any payment to
              CTVBuyer. In such case, Advertiser will pay to CTVBuyer such additional amounts as are
              necessary so that each net payment to CTVBuyer (i.e., after Tax withholding) is no less than
              the amount that would otherwise have been payable under the Agreement. If CTVBuyer is held
              responsible for any Taxes arising from the Agreement (aside from Taxes based on
              CTVBuyer&rsquo;s net income), Advertiser will reimburse CTVBuyer for such payments.
            </>,
          ]}
        />
      </Section>

      <Section heading="Additional Requirements">
        <Bullets
          items={[
            <>
              Advertiser will comply with all applicable laws, regulations and rules, including all
              applicable privacy and data protection laws, and will not infringe any third party rights.
              Advertiser is solely responsible for assessing its own legal obligations related to its use
              of the Services, including any notice, consent and opt out requirements arising from
              collection and use of end user data in connection with the display of Ads and interaction
              with Ads and Advertiser&rsquo;s online properties. Advertiser will obtain all rights
              necessary to perform tracking and analytics for its campaigns.
            </>,
            <>
              Advertiser will not attempt to circumvent any CTVBuyer or Provider security measures; share
              access credentials with any third party; resell the Services; reproduce, distribute,
              prepare derivative works of, translate, reverse engineer, reverse compile or disassemble the
              Services; distribute viruses or malware; use the Services to create or share inappropriate
              content or material; or use the Services to gather competitive intelligence. Advertiser
              shall not copy or otherwise reproduce the Services or remove, alter or destroy any
              proprietary markings appearing in or on the Services. Unless this Agreement is amended to
              provide otherwise, Advertiser will not use any CTVBuyer pixels or tags.
            </>,
            <>
              If Advertiser asks CTVBuyer to take actions on behalf of Advertiser or otherwise assist with
              Advertiser&rsquo;s use of the Services, Advertiser consents to the actions and/or inactions
              that CTVBuyer takes on its behalf and retains sole responsibility for such assisted use of
              the Services.
            </>,
            <>
              In making the Services available to Advertiser, CTVBuyer will comply with all applicable
              laws, regulations and rules, including laws related to privacy and data protection.
              Advertiser acknowledges that CTVBuyer does not control the privacy practices of Providers
              and other third parties, and that such entities&rsquo; practices may be different than
              CTVBuyer&rsquo;s.
            </>,
            <>
              CTVBuyer and Providers may use and share the data collected or generated in connection with
              the Services for any business purpose, including to provide, support, improve, enhance, and
              customize the Services, develop additional services, send recommendations and suggestions,
              collect and pay fees, conduct administrative activities necessary to maintain and provide
              the Services, enforce this Agreement, and take action in any dispute or legal proceeding
              involving Advertiser, prevent fraud, misappropriation, infringement, identity theft and
              other illegal activities and misuse of the Services.
            </>,
          ]}
        />
      </Section>

      <Section heading="Marketing">
        <p>
          CTVBuyer may use Advertiser&rsquo;s name and logo in marketing materials (including its
          website) and communications/press releases. Advertiser will not make any public statement
          about this Agreement without CTVBuyer&rsquo;s prior written agreement (email sufficing).
        </p>
      </Section>

      <Section heading="Confidentiality">
        <p>
          The terms of this Agreement are the Confidential Information of each party (not to be disclosed
          by a party without the written consent of the other). CTVBuyer Confidential Information
          includes the terms of the Agreement and the details and information regarding the Services,
          Providers and Inventory that Advertiser may be exposed to during the performance of the
          Agreement, including information about CTVBuyer&rsquo;s and Providers&rsquo; business
          practices, documentation, presentations and technical support materials. The receiving party
          will use the same care to protect Confidential Information as it uses for its own similar
          information, but in no event less than reasonable care, and will use Confidential Information
          only for the purpose of fulfilling its obligations under the Agreement. CTVBuyer may disclose
          Advertiser Confidential Information to Providers. The receiving party will promptly return or
          destroy the other party&rsquo;s Confidential Information upon request of the other party.
          Confidential Information does not include information that: (a) is or becomes part of the public
          domain through no fault of the receiving party; (b) was already in possession of the receiving
          party; or (c) was independently developed by the receiving party without violation of this
          Section 13. The receiving party may disclose Confidential Information if required to do so by
          law, if the receiving party provides the disclosing party with prompt notice and complies with
          any protective order imposed on such disclosure.
        </p>
      </Section>

      <Section heading="Indemnity">
        <Bullets
          items={[
            <>
              CTVBuyer will indemnify, defend, and hold harmless Advertiser, its officers, directors,
              shareholders and employees (&ldquo;Advertiser Parties&rdquo;) against all liability, claims,
              costs, damages, settlements, and expenses (including interest, penalties, attorney fees and
              expert witness fees) incurred by the Advertiser Parties from third party claims (a) that
              CTVBuyer&rsquo;s proprietary technology that provides the Services, in the form provided by
              CTVBuyer, infringes any US patent or other third party intellectual property right; or (b)
              arising from or related to a claim by a Provider that CTVBuyer is not entitled to provide
              the Services as described in this Agreement. Advertiser will indemnify, defend, and hold
              harmless CTVBuyer, its Providers and each of their officers, directors, shareholders,
              employees and agents (&ldquo;Services Parties&rdquo;) against all liability, claims, costs,
              damages, settlements, and expenses (including interest, penalties, attorney fees and expert
              witness fees) incurred by the Services Parties (including claims made against CTVBuyer by a
              Provider) from third party claims arising from or related to (i) breach of any Advertiser
              representation and warranty, (ii) violation of this Agreement, or (iii) Advertiser, Ads or
              Advertiser&rsquo;s website, products or services.
            </>,
            <>
              The indemnified party will provide the indemnitor with prompt notice (in any event within 15
              days of the service of the citation or summons) of any claim (provided that the failure to
              promptly notify shall only relieve indemnitor of its obligation to the extent it can
              demonstrate material prejudice from such failure) and at the indemnitor&rsquo;s expense,
              provide assistance reasonably necessary to defend such claim. Indemnitor may elect to direct
              the defense or settlement of any proceeding by giving written notice to the indemnified
              party. Without the indemnified party&rsquo;s prior written consent, which, in the case of (i)
              and (ii) below, shall not be unreasonably withheld or delayed, the indemnitor shall not
              enter into any settlement or compromise that (i) would not fully absolve the indemnified
              party of liability, (ii) would contain any admission of or stipulation to any guilt, fault,
              liability or wrongdoing on the part of the indemnified party, or (iii) would restrict or
              limit the indemnified party&rsquo;s business or operations. An indemnified party will not
              settle, or consent to any entry of judgment, in any proceeding without first obtaining
              written consent of the indemnitor.
            </>,
            <>
              If CTVBuyer&rsquo;s right to provide the Services is enjoined or in CTVBuyer&rsquo;s opinion
              is likely to be enjoined, CTVBuyer may obtain the right to continue providing the Services,
              replace or modify the Services so that they become non-infringing or terminate this
              Agreement. CTVBuyer&rsquo;s obligations as stated in this Section 9 are CTVBuyer&rsquo;s sole
              and exclusive liability and Advertiser&rsquo;s sole remedy as to infringement.
            </>,
          ]}
        />
      </Section>

      <Section heading="Cancelation and Termination">
        <p>
          Advertiser can cancel month-to-month campaigns that are not paid up front, effective at the end
          of the then-current month. Campaigns committed or paid up front cannot be canceled and will not
          be refunded. CTVBuyer may suspend or terminate Advertiser&rsquo;s account and this Agreement
          upon notice for any reason or no reason. If CTVBuyer suspends or closes Advertiser&rsquo;s
          account, Advertiser may not re-open an account without CTVBuyer&rsquo;s prior written approval.
          Termination or suspension will not prevent CTVBuyer or Providers from taking any other actions
          or measures available to them under the Agreement, as applicable. Upon suspension or
          termination, Advertiser will not be able to access its account.
        </p>
        <p>
          Provisions that by their nature do not necessarily terminate upon termination of the Agreement
          shall survive.
        </p>
      </Section>

      <Section heading="Disclaimers">
        <p>
          CTVBUYER, ITS PROVIDERS, AFFILIATES, OFFICERS, DIRECTORS, SHAREHOLDERS, EMPLOYEES, ADVISORS,
          CONTRACTORS, LICENSORS, LICENSEES AND AGENTS (&ldquo;SERVICES PARTIES&rdquo;) MAKE NO WARRANTY
          OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY OR OTHERWISE, INCLUDING WITHOUT LIMITATION
          WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR USE, AND NON-INFRINGEMENT. THE SERVICES
          ARE PROVIDED AS IS, WITH NO WARRANTY AS TO CONTINUOUS OR ERROR FREE SERVICE.
        </p>
        <p>
          CTVBuyer is not responsible for THE Inventory or other third party services and makes no
          representations as to the Providers and their services, including their privacy practices.
          Advertiser acknowledges that Providers may change the Inventory or other services, suspend or
          discontinue access to the Inventory/services in whole or in part, and/or terminate
          CTVBuyer&rsquo;s ability to provide all or part of the Inventory/services, at any time, with or
          without notice. CTVBuyer reserves the right to provide substitute inventory or other services
          in its discretion.
        </p>
        <p>
          CTVBUYER AND THE SERVICES DO NOT PROVIDE LEGAL, REGULATORY OR COMPLIANCE ADVICE. ADVERTISER IS
          SOLELY RESPONSIBLE FOR DETERMINING AND COMPLYING WITH ALL LEGAL, REGULATORY AND OTHER
          REQUIREMENTS AND FOR OBTAINING ALL NECESSARY RIGHTS RELATED TO THE ADS AND ITS CAMPAIGNS.
        </p>
      </Section>

      <Section heading="Limitation of Liability">
        <p>
          EXCEPT FOR DAMAGES ARISING UNDER SECTIONS 6.1 OR 6.2 AND OBLIGATIONS ARISING UNDER SECTION 9
          (INDEMNITY) (THE &ldquo;EXCLUDED CLAIMS&rdquo;), IN NO EVENT SHALL EITHER PARTY BE LIABLE FOR
          INDIRECT, SPECIAL, INCIDENTAL OR CONSEQUENTIAL DAMAGES, INCLUDING, BUT NOT LIMITED TO, LOSS OF
          PROFITS, LOSS OF REVENUES, OR LOSS OF OPPORTUNITIES, ARISING OUT OF THIS AGREEMENT OR ANY
          EXHIBIT, EVEN IF SUCH PARTY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. EXCEPT FOR THE
          EXCLUDED CLAIMS AND ADVERTISER&rsquo;S OBLIGATION TO PAY ALL FEES DUE UNDER THIS AGREEMENT, IN
          NO CASE SHALL EITHER PARTY&rsquo;S MAXIMUM LIABILITY ARISING OUT OF THIS AGREEMENT, WHETHER
          BASED UPON WARRANTY, CONTRACT, NEGLIGENCE, TORT, STRICT LIABILITY, OR OTHERWISE, EXCEED IN THE
          AGGREGATE, THE AMOUNT PAID OR OWED TO CTVBUYER UNDER THIS AGREEMENT FOR THE TWLEVE MONTHS PRIOR
          TO THE DATE THE CAUSE OF ACTION AROSE. The limitations and exclusions contained in this
          Agreement represent the parties&rsquo; agreement based upon the perceived level of risk
          associated with this Agreement and the payments made hereunder.
        </p>
      </Section>

      <Section heading="General">
        <Bullets
          items={[
            <>
              CTVBuyer reserves the right to continually evolve the Services without notice. If Advertiser
              provides CTVBuyer with any feedback, CTVBuyer may use that feedback without accounting,
              attribution or compensation to Advertiser.
            </>,
            <>
              Notices may be given via email to a party&rsquo;s primary contact and shall be effective
              immediately upon sending provided that the sender does not receive a response that the email
              did not successfully go through. Notices may also be given via personal delivery.
            </>,
            <>
              This is the entire agreement of the parties as to this subject matter. Any terms that differ
              from those in this Agreement that Advertiser includes in an order form, purchase order or
              similar document are of no force and effect.
            </>,
            <>
              Neither party can assign this Agreement without the other party&rsquo;s written consent,
              except that either party may assign this Agreement without consent to a corporate parent,
              subsidiary or affiliate or to an acquirer of the other party&rsquo;s business. Advertiser
              shall promptly provide written notice to CTVBuyer if all or substantially all of
              Advertiser&rsquo;s assets, stock or business are the subject of an acquisition, sale, or
              merger.
            </>,
            <>
              Any claim arising out of or related to this Agreement must be brought in the initiating
              party&rsquo;s individual capacity and not as a plaintiff or class member in any class action
              or other similar proceeding.
            </>,
            <>
              The parties have been represented in the negotiation and execution of this Agreement by
              legal counsel of their own choice or have voluntarily declined to seek such counsel.
            </>,
            <>
              This Agreement is governed by the laws of the State of New York, excluding conflicts of laws
              principles. The parties will resolve any disputes in the state or federal courts of New York
              City, to whose exclusive jurisdiction and venue they irrevocably submit.
            </>,
            <>
              If any provision of this Agreement is unenforceable, the validity and enforceability of the
              remaining provisions will not be affected. No waiver will be effective unless it is in an
              explicit writing and signed by the waiving party.
            </>,
            <>
              Neither party is liable for failure or delay in performing its obligations because of causes
              beyond its reasonable control, including acts of God, terrorism, war, riots, fire,
              earthquake, flood or degradation or failure of third-party networks or communications
              infrastructure.
            </>,
          ]}
        />
      </Section>

      <p className="pt-2 text-[11px] text-slate-400">
        Version {ADVERTISER_TERMS_VERSION} · {ADVERTISER_TERMS_EFFECTIVE_DATE}
      </p>
    </div>
  );
};

export default AdvertiserTermsContent;
